// The PO panel's Builds tab for make POs (Mitch, 2026-11-04: "so many boxes and buttons"): what Hand Held Legend sends
// for the builds (each consigned part once: the builds it goes into, still needed, at the CM, short), one table of the
// builds (units and price edited in it, built, stage, status), and the build picked below it with its Parts (the
// components it needs, who supplies each, reserved and short at the CM), Charges (the CM's price for it) and Units built
// (report output, components consumed at the CM, and scrap; the reports so far). Each is the usual table.
import { Plus, RefreshCw } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import type { Supply } from "../../../../shared/boms.ts";
import { buildSupplyText } from "../../../../shared/build-docs.ts"; // who supplies each part, naming who
import { MAKE_STAGE_LABELS, MAKE_STAGES, type MakeOrderState, SCRAP_REASON_LABELS, type ScrapReason } from "../../../../shared/consignment.ts";
import type { CmReport, Item, MakeOrder, Po, PoLine } from "../../../../shared/db.gen.ts";
import { linesEditable } from "../../../../shared/po.ts";
import { backersLeft, hhlDone, outstanding } from "../../../../shared/receiving.ts";
import { bomPath, useBomIndex } from "../../lib/boms.ts";
import { ago, money, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Input, Select, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { LineCharges } from "../Charges.tsx"; // Charges ---
import { consignmentPath, makeOrderStates, STATUS_LABELS, STATUS_TONES, useCms } from "./lib.ts";
import { ItemPicker, parseQty, QtyInput } from "./parts.tsx";
import { reasonOptions } from "./ScrapDialog.tsx";

const REPORTABLE = new Set(["sent", "acknowledged", "in_production", "shipped"]);

export function MakeOrderView({ po, lines }: { po: Po; lines: PoLine[] }) {
  const makeOrders = useRows("make_order");
  const locations = useRows("location");
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const cms = useCms();
  const states = useMemo(() => makeOrderStates(cms), [cms]);
  const byLine = useMemo(() => new Map(makeOrders.filter((m) => m.po_id === po.id).map((m) => [m.po_line_id, m])), [makeOrders, po.id]);
  const cmId = [...byLine.values()][0]?.cm_location_id;
  const cm = locations.find((l) => l.id === cmId) ?? locations.find((l) => l.is_cm && l.vendor_id === po.vendor_id);
  const editable = linesEditable(po.status) && can("make_order.create");
  // The build open below the table: the one picked, else the first
  const [picked, pick] = useState<string | null>(null);
  const open = lines.find((l) => l.id === picked) ?? lines[0];

  return (
    <div className="flex flex-col">
      {!cm && <div className="border-b border-line px-4 py-2 text-[12px] text-warn">This vendor has no CM site yet: set one up on the Consignment page.</div>}
      <BackersBar po={po} lines={lines} byLine={byLine} />
      {cm && <HhlSends po={po} lines={lines} byLine={byLine} states={states} itemById={itemById} cmName={cm.name} cmId={cm.id} />}
      <BuildsTable po={po} lines={lines} byLine={byLine} states={states} itemById={itemById} openId={open?.id ?? null} onOpen={pick} />
      {editable && cm && <AddBuild po={po} />}
      {open &&
        ((mo) => (mo ? <Build key={open.id} po={po} line={open} mo={mo} state={states.get(mo.id)} /> : <NoSnapshot po={po} line={open} editable={editable} />))(
          byLine.get(open.id),
        )}
    </div>
  );
}

type SendRow = { id: string; sku: string; name: string; builds: string[]; remaining: number; reserved: number; short: number; atCm: number };

/**
 * What Hand Held Legend sends for these builds (Mitch, 2026-11-04: "Built at Elecrow from HHL's consigned parts" was "only
 * partially true … something clearly indicating what those are, and where they are going to be used is necessary"): each
 * consigned part once, the builds it goes into, what the units still to build take, what's set aside for them at the CM
 * and what's short. Parts the CM buys or makes are its own, so they aren't here (each build's Parts has them).
 */
function HhlSends({
  po,
  lines,
  byLine,
  states,
  itemById,
  cmName,
  cmId,
}: {
  po: Po;
  lines: PoLine[];
  byLine: Map<string, MakeOrder>;
  states: Map<string, MakeOrderState>;
  itemById: Map<string, Item>;
  cmName: string;
  cmId: string;
}) {
  const cmStock = useCms().get(cmId);
  const onHand = useMemo(() => new Map((cmStock?.stock ?? []).map((l) => [l.itemId, l.onHand])), [cmStock]);
  const rows = useMemo(() => {
    const out = new Map<string, SendRow>();
    for (const line of lines) {
      const mo = byLine.get(line.id);
      const state = mo ? states.get(mo.id) : undefined;
      for (const c of state?.components ?? []) {
        if (!c.consigned) continue;
        const row = out.get(c.item_id) ?? {
          id: c.item_id,
          sku: itemById.get(c.item_id)?.sku ?? c.sku,
          name: itemById.get(c.item_id)?.name ?? c.name,
          builds: [],
          remaining: 0,
          reserved: 0,
          short: 0,
          atCm: onHand.get(c.item_id) ?? 0,
        };
        if (!row.builds.includes(line.sku)) row.builds.push(line.sku);
        row.remaining += c.remaining;
        row.reserved += c.reserved;
        row.short += c.short;
        out.set(c.item_id, row);
      }
    }
    return [...out.values()];
  }, [lines, byLine, states, itemById, onHand]);
  const columns: Column<SendRow>[] = [
    { key: "sku", header: "SKU", width: "minmax(130px,0.8fr)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    { key: "name", header: "Name", width: "minmax(160px,1.2fr)", cell: (r) => <span className="truncate">{r.name}</span> },
    {
      key: "builds",
      header: "Goes into",
      width: "minmax(110px,0.8fr)",
      value: (r) => r.builds.join(", "),
      cell: (r) => <span className="truncate font-mono text-[12px] text-muted">{r.builds.join(", ")}</span>,
    },
    { key: "remaining", header: "Still needed", width: "100px", align: "right", cell: (r) => <span className="font-mono">{qty(r.remaining) || "0"}</span> },
    { key: "atCm", header: `At ${cmName}`, width: "100px", align: "right", cell: (r) => <span className="font-mono text-muted">{qty(r.atCm) || "0"}</span> },
    {
      key: "short",
      header: "Short",
      width: "110px",
      align: "right",
      cell: (r) => (r.short > 0 ? <span className="font-mono font-medium text-danger">{qty(r.short)} short</span> : <span className="text-ok">enough</span>),
    },
  ];
  return (
    <section className="flex flex-col gap-1.5 border-b border-line px-4 py-3">
      <div className="flex items-baseline gap-2">
        <h3 className="font-semibold">What Hand Held Legend sends</h3>
        <span className="truncate text-[12px] text-muted">
          {rows.length
            ? `Consigned to ${cmName} for these builds; ${cmName} buys or makes everything else.`
            : `Nothing: ${cmName} buys or makes every part of these builds.`}
        </span>
        {po.status !== "draft" && (
          <Link to={consignmentPath(cmId)} className="ml-auto shrink-0 text-[12px] text-accent-text hover:underline">
            {cmName}'s stock
          </Link>
        )}
      </div>
      {rows.length > 0 && <DataTable fit rows={rows} columns={columns} exportName="What HHL sends" pageFilters={false} rowNoun={["part", "parts"]} />}
    </section>
  );
}

type BuildRow = PoLine & { mo: MakeOrder | undefined; state: MakeOrderState | undefined; amount: number };

/** One row per build: units and price (edited here while the lines can change), built so far, stage and status; a row opens it below. */
function BuildsTable({
  po,
  lines,
  byLine,
  states,
  itemById,
  openId,
  onOpen,
}: {
  po: Po;
  lines: PoLine[];
  byLine: Map<string, MakeOrder>;
  states: Map<string, MakeOrderState>;
  itemById: Map<string, Item>;
  openId: string | null;
  onOpen: (id: string) => void;
}) {
  const editable = linesEditable(po.status) && can("po_line.update");
  const rows: BuildRow[] = lines.map((l) => {
    const mo = byLine.get(l.id);
    return { ...l, mo, state: mo ? states.get(mo.id) : undefined, amount: Number(l.qty) * Number(l.unit_price ?? 0) };
  });
  const columns: Column<BuildRow>[] = [
    { key: "position", header: "#", width: "40px", align: "right", value: (r) => r.position, cell: (r) => <span className="text-faint">{r.position}</span> },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,0.7fr)",
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <Thumb src={itemById.get(r.item_id)?.photo_url} size={20} />
          <span className="truncate font-mono text-[12px] font-medium">{r.sku}</span>
        </span>
      ),
    },
    { key: "name", header: "Name", width: "minmax(170px,1.4fr)", cell: (r) => <span className="truncate">{r.name}</span> },
    {
      key: "qty",
      header: "Units",
      width: "90px",
      align: "right",
      value: (r) => Number(r.qty),
      cell: (r) => <span className="font-mono">{qty(r.qty)}</span>,
      ...(editable
        ? {
            edit: {
              get: (r: BuildRow) => String(Number(r.qty)),
              save: (r: BuildRow, text: string) => {
                const n = parseQty(text);
                if (n && n > 0) mutate("po_line.update", { id: r.id, patch: { qty: n } });
              },
            },
          }
        : {}),
    },
    {
      key: "unit_price",
      header: `Price (${po.currency})`,
      width: "110px",
      align: "right",
      value: (r) => Number(r.unit_price ?? 0),
      cell: (r) => <span className="font-mono">{r.unit_price == null ? "—" : money(Number(r.unit_price), po.currency)}</span>,
      ...(editable
        ? {
            edit: {
              get: (r: BuildRow) => (r.unit_price == null ? "" : String(Number(r.unit_price))),
              save: (r: BuildRow, text: string) => {
                const n = Number(text.replace(/[$,\s]/g, ""));
                if (Number.isFinite(n) && n >= 0) mutate("po_line.update", { id: r.id, patch: { unit_price: n } });
              },
            },
          }
        : {}),
    },
    { key: "amount", header: "Amount", width: "120px", align: "right", cell: (r) => <span className="font-mono">{money(r.amount, po.currency)}</span> },
    {
      key: "built",
      header: "Built",
      width: "110px",
      align: "right",
      value: (r) => r.state?.output ?? 0,
      cell: (r) => (
        <span className="font-mono text-muted">
          {qty(r.state?.output ?? 0) || "0"} of {qty(r.qty)}
        </span>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      width: "120px",
      value: (r) => (r.mo ? MAKE_STAGE_LABELS[r.mo.stage] : ""),
      cell: (r) => <span className="truncate text-muted">{r.mo ? MAKE_STAGE_LABELS[r.mo.stage] : "No BOM snapshot"}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "150px",
      value: (r) => (r.state ? STATUS_LABELS[r.state.status] : ""),
      cell: (r) => (r.state ? <Badge tone={STATUS_TONES[r.state.status]}>{STATUS_LABELS[r.state.status]}</Badge> : null),
    },
  ];
  return (
    <section className="flex flex-col gap-1.5 px-4 py-3">
      <div className="flex items-baseline gap-2">
        <h3 className="font-semibold">Builds</h3>
        <span className="truncate text-[12px] text-muted">
          {editable
            ? "Click units or a price to change it. A build opens below: its parts, the CM's charges, units built."
            : "A build opens below: its parts, the CM's charges, units built."}
        </span>
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        editing={editable}
        activeId={openId}
        onOpen={(r) => onOpen(r.id)}
        exportName="Builds"
        pageFilters={false}
        rowNoun={["build", "builds"]}
        defaultSort={{ key: "position" }}
      />
    </section>
  );
}

/**
 * Pre-sales --- the units for backers the CM still has to build or ship: the PO stays open for them (received once
 * they're reported built or shipped). If the CM won't build them, staff release them back to planning.
 */
function BackersBar({ po, lines, byLine }: { po: Po; lines: PoLine[]; byLine: Map<string, MakeOrder> }) {
  const vendors = useRows("vendor");
  const [busy, setBusy] = useState(false);
  const counted = lines.map((l) => ({ ...l, hhl_closed: po.hhl_share_closed, qty_output: byLine.get(l.id)?.qty_output ?? null }));
  const left = counted.reduce((t, l) => t + backersLeft(l), 0);
  if (left <= 0 || ["draft", "received", "closed", "cancelled"].includes(po.status)) return null;
  const v = vendors.find((x) => x.id === po.vendor_id);
  const cm = v?.short_name || v?.name || "The CM";
  const due = counted.filter((l) => !hhlDone(l)).reduce((t, l) => t + outstanding(l), 0);
  const release = async () => {
    if (
      !confirm(
        `${cm} won't build the other ${qty(left)} for backers on ${po.number}?\n\nThey go back to planning: another open make PO on the run takes them, else they show as still to build. ${po.number} is received with what's built or shipped for backers so far. This can't be undone here.`,
      )
    )
      return;
    setBusy(true);
    const { ok, result } = await mutateWithResult("po.release_backers", { id: po.id });
    setBusy(false);
    if (ok) toast.ok((result as { note?: string })?.note ?? "Released");
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2/50 px-4 py-2 text-[12px]">
      <span>
        <span className="font-medium">
          {cm} still has {qty(left)} to build or ship for backers.
        </span>{" "}
        <span className="text-muted">
          {po.number} stays open until they're reported built or shipped
          {po.hhl_share_closed ? "; nothing else is coming to HHL" : due > 0 ? `; ${qty(due)} still expected at HHL` : "; everything for HHL is in"}.
        </span>
      </span>
      {po.hhl_share_closed && can("po.reopen_hhl_share") && (
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          disabled={busy}
          title="Undo Nothing else is coming: HHL expects the rest of its share again (and Shopify shows it as incoming)"
          onClick={async () => {
            setBusy(true);
            const { ok } = await mutateWithResult("po.reopen_hhl_share", { id: po.id });
            setBusy(false);
            if (ok) toast.ok("HHL's share is open again");
          }}
        >
          Reopen HHL's share
        </Button>
      )}
      {can("po.release_backers") && (
        <Button
          size="sm"
          variant="ghost"
          className={po.hhl_share_closed ? "" : "ml-auto"}
          disabled={busy || due > 0}
          title={
            due > 0
              ? "Receive HHL's share first (or post a receipt with Nothing else is coming)"
              : "They go back to planning: another make PO, else still to build"
          }
          onClick={release}
        >
          {cm} won't build them
        </Button>
      )}
    </div>
  );
}

function NoSnapshot({ po, line, editable }: { po: Po; line: PoLine; editable: boolean }) {
  const [id] = useState(() => crypto.randomUUID());
  const use = async () => {
    const { ok, result } = await mutateWithResult("make_order.create", { id, po_id: po.id, po_line_id: line.id });
    if (ok) for (const w of (result as { warnings?: string[] }).warnings ?? []) toast.info(w);
  };
  return (
    <div className="flex items-center gap-3 border-b border-line px-4 py-3">
      <span className="font-mono text-[12px]">{line.sku}</span>
      <span className="min-w-0 flex-1 truncate">{line.name}</span>
      <span className="text-[12px] text-warn">No BOM snapshot: the CM's parts for it aren't reserved or consumed</span>
      {editable && (
        <Button size="sm" onClick={use}>
          Use its BOM
        </Button>
      )}
    </div>
  );
}

function AddBuild({ po }: { po: Po }) {
  const ix = useBomIndex();
  const [item, setItem] = useState<Item | null>(null);
  const [n, setN] = useState("");
  const [busy, setBusy] = useState(false);
  const hasBom = (i: Item) => ix.families.has(i.source === "shopify" && i.shopify_product_id ? `product:${i.shopify_product_id}` : `item:${i.id}`);
  const add = async () => {
    const count = parseQty(n);
    if (!item || !count || count <= 0) return toast.error("Pick an item and how many");
    setBusy(true);
    const { ok, result } = await mutateWithResult("make_order.create", { id: crypto.randomUUID(), po_id: po.id, item_id: item.id, qty: count });
    setBusy(false);
    if (!ok) return;
    for (const w of (result as { warnings?: string[] }).warnings ?? []) toast.info(w);
    setItem(null);
    setN("");
  };
  return (
    <div className="flex items-center gap-2 px-4 py-3">
      <Plus className="size-4 text-faint" />
      {item ? (
        <>
          <span className="font-mono text-[12px]">{item.sku}</span>
          <span className="truncate">{item.name}</span>
          <button type="button" className="text-[12px] text-accent-text hover:underline" onClick={() => setItem(null)}>
            change
          </button>
        </>
      ) : (
        <ItemPicker className="w-80" allow={hasBom} onPick={setItem} placeholder="Add a build: an item with a BOM…" />
      )}
      <div className="w-24">
        <QtyInput aria-label="Units to build" value={n} onChange={setN} />
      </div>
      <Button size="sm" variant="primary" disabled={busy || !item} onClick={add}>
        Add build
      </Button>
      <span className="text-[12px] text-faint">
        Its price is the vendor's for the item, else what its BOM says the CM charges (services and parts the CM buys).
      </span>
    </div>
  );
}

type BuildPart = "parts" | "charges" | "output";

/** The build picked in the table: its BOM revision and stage, then one of its Parts, Charges and Output. */
function Build({ po, line, mo, state }: { po: Po; line: PoLine; mo: MakeOrder; state: MakeOrderState | undefined }) {
  const [open, setOpen] = useState<BuildPart>("parts");
  const reports = useRows("cm_report");
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const mine = useMemo(() => reports.filter((r) => r.make_order_id === mo.id).sort((a, b) => b.reported_at.localeCompare(a.reported_at)), [reports, mo.id]);
  const canReport = REPORTABLE.has(po.status) && can("make_order.report_output");
  const canRefresh = Number(mo.qty_output) === 0 && !mine.length && linesEditable(po.status) && can("make_order.refresh_bom");

  const refresh = async () => {
    const { ok, result } = await mutateWithResult("make_order.refresh_bom", { id: mo.id });
    if (ok) toast.ok(`Now built from revision ${(result as { bom_revision: number }).bom_revision}`);
  };

  return (
    <section className="border-t border-line">
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-semibold">
            <span className="font-mono">{line.sku}</span> {line.name}
          </span>
          <span className="text-[12px] text-muted">
            Built from{" "}
            {mo.bom_id ? (
              <Link to={bomPath(mo.bom_id, mo.item_id)} className="hover:underline">
                BOM revision {mo.bom_revision}
              </Link>
            ) : (
              `BOM revision ${mo.bom_revision ?? "?"}`
            )}
            {canRefresh && (
              <button
                type="button"
                onClick={refresh}
                className="ml-2 inline-flex items-center gap-1 text-accent-text hover:underline"
                title="Take its BOM's current revision again"
              >
                <RefreshCw className="size-3" /> use the current revision
              </button>
            )}
            {state && (
              <>
                {" "}
                · delivered {qty(state.delivered) || "0"} · waiting at the CM {qty(state.atCm) || "0"}
                {state.open ? ` · can build ${qty(state.buildable) || "0"} now` : ""}
              </>
            )}
          </span>
        </div>
        <div className="w-40 shrink-0">
          <Select
            aria-label="Stage"
            value={mo.stage}
            disabled={!can("make_order.update")}
            onChange={(e) => mutate("make_order.update", { id: mo.id, patch: { stage: e.target.value as MakeOrder["stage"] } })}
            options={MAKE_STAGES.map((s) => ({ value: s, label: MAKE_STAGE_LABELS[s] }))}
            className="h-7 text-[12px]"
          />
        </div>
      </div>
      <div className="flex items-center gap-1 px-3 pt-2 pb-2">
        {(
          [
            ["parts", "Parts"],
            ["charges", "Charges"],
            ["output", mine.length ? `Units built (${mine.length})` : "Units built"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={open === key}
            onClick={() => setOpen(key)}
            className={cx(
              "rounded-md px-2.5 py-1 text-[12px]",
              open === key ? "bg-accent-soft font-medium text-accent-text" : "text-muted hover:bg-hover hover:text-text",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {open === "parts" && <Components mo={mo} state={state} itemById={itemById} />}
      {open === "charges" && <LineCharges po={po} line={line} />}
      {open === "output" && (
        <>
          {canReport && <ReportForm mo={mo} state={state} itemById={itemById} />}
          {mine.length > 0 && <Reports reports={mine} itemById={itemById} />}
          {!canReport && !mine.length && (
            <p className="px-4 pb-3 text-[12px] text-faint">{po.status === "draft" ? "Mark it as sent to report units built." : "Nothing reported."}</p>
          )}
        </>
      )}
    </section>
  );
}

type ReportRow = CmReport & { scrap: string; by: string };

/** A build's reports (output, scrap), newest first: the usual table, its menus by its label. */
function Reports({ reports, itemById }: { reports: CmReport[]; itemById: Map<string, Item> }) {
  const reportLines = useRows("cm_report_line");
  const users = useRows("app_user");
  const rows = useMemo<ReportRow[]>(
    () =>
      reports.map((r) => ({
        ...r,
        scrap: reportLines
          .filter((l) => l.report_id === r.id && l.kind === "scrap")
          .map((l) => `${itemById.get(l.item_id)?.sku ?? "?"} −${qty(l.qty)} (${SCRAP_REASON_LABELS[l.reason as ScrapReason] ?? l.reason})`)
          .join(", "),
        by: r.reported_by ? (users.find((u) => u.id === r.reported_by)?.name ?? "") : "",
      })),
    [reports, reportLines, users, itemById],
  );
  const columns: Column<ReportRow>[] = [
    {
      key: "reported_at",
      header: "When",
      width: "110px",
      value: (r) => r.reported_at,
      cell: (r) => (
        <span className="truncate text-[12px] text-faint" title={new Date(r.reported_at).toLocaleString()}>
          {ago(r.reported_at)}
        </span>
      ),
    },
    {
      key: "what",
      header: "Report",
      width: "130px",
      value: (r) => (r.kind === "output" ? `${qty(r.qty_output)} built` : "Scrap"),
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-1.5 text-[12px]">
          <span className="truncate">{r.kind === "output" ? `${qty(r.qty_output)} built` : "Scrap"}</span>
          {r.auto && <Badge>from a receipt</Badge>}
        </span>
      ),
    },
    { key: "scrap", header: "Scrap", width: "minmax(160px,1.5fr)", cell: (r) => <span className="truncate text-[12px] text-muted">{r.scrap}</span> },
    {
      key: "notes",
      header: "Note",
      width: "minmax(140px,1fr)",
      cell: (r) => (
        <span className="truncate text-[12px] text-muted" title={r.notes || undefined}>
          {r.notes}
        </span>
      ),
    },
    { key: "by", header: "By", width: "120px", cell: (r) => <span className="truncate text-[12px] text-muted">{r.by}</span> },
  ];
  return (
    <div className="px-4 pb-3">
      <div className="flex items-center gap-2 pb-1">
        <span className="text-[11px] text-faint">Reports</span>
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "reported_at", desc: true }}
        rowNoun={["report", "reports"]}
        exportName="Build reports"
        pageFilters={false}
      />
    </div>
  );
}

type ComponentRow = MakeOrderState["components"][number] & { id: string; sku: string; name: string; atCm: number | null };

/** The build's parts (its BOM snapshot × units), what's used, still needed, at the CM, set aside and short: the usual table. */
function Components({ mo, state, itemById }: { mo: MakeOrder; state: MakeOrderState | undefined; itemById: Map<string, Item> }) {
  const cmStock = useCms().get(mo.cm_location_id);
  // Who supplies each part, naming who (shared/build-docs.ts buildSupplyText(), as the CM's documents page and PDF say
  // it): "Elecrow makes it", "Hand Held Legend sends it (from Favor Union)", its preferred vendor
  const vendors = useRows("vendor");
  const links = useRows("vendor_item");
  const po = useRows("po").find((p) => p.id === mo.po_id);
  const nameOf = (id: string | undefined) => {
    const v = vendors.find((x) => x.id === id);
    return v ? v.short_name || v.name : "";
  };
  const cmName = nameOf(po?.vendor_id);
  const fromOf = (itemId: string) => {
    const mine = links.filter((l) => l.item_id === itemId);
    return nameOf((mine.find((l) => l.is_preferred) ?? mine[0])?.vendor_id) || null;
  };
  const supplyText = (c: { supply: Supply; kind: string; item_id: string }) =>
    c.kind === "service"
      ? "Service"
      : buildSupplyText({ supply: c.supply, inside: false, cm: cmName, from: c.supply === "hhl_consigns" ? fromOf(c.item_id) : null });
  const onHand = useMemo(() => new Map((cmStock?.stock ?? []).map((l) => [l.itemId, l])), [cmStock]);
  const rows = useMemo<ComponentRow[]>(
    () =>
      (state?.components ?? []).map((c) => ({
        ...c,
        id: `${c.item_id}|${c.supply}`,
        sku: itemById.get(c.item_id)?.sku ?? c.sku,
        name: itemById.get(c.item_id)?.name ?? c.name,
        atCm: c.consigned ? (onHand.get(c.item_id)?.onHand ?? 0) : null,
      })),
    [state, itemById, onHand],
  );
  if (!state) return <div className="px-4 py-2 text-[12px] text-warn">The CM site isn't marked as one any more, so this build's parts can't be shown.</div>;
  // Parts HHL doesn't consign (the CM buys or makes them, services) are faint, as they're the CM's to have.
  const dim = (c: ComponentRow, node: ReactNode, cls = "") => (
    <span className={cx("truncate font-mono text-[12px]", cls, !c.consigned && "text-faint")}>{node}</span>
  );
  const columns: Column<ComponentRow>[] = [
    { key: "sku", header: "SKU", width: "120px", cell: (c) => dim(c, c.sku) },
    {
      key: "name",
      header: "Name",
      width: "minmax(160px,1fr)",
      cell: (c) => (
        <span className={cx("flex min-w-0 items-center gap-1", !c.consigned && "text-faint")}>
          <span className="truncate">{c.name}</span>
          {c.source === "shopify" && c.consigned && <Badge>Shopify</Badge>}
        </span>
      ),
    },
    {
      key: "supply",
      header: "Who supplies it",
      width: "minmax(170px,0.8fr)",
      value: (c) => supplyText(c),
      cell: (c) => (
        <span className={cx("truncate text-[12px]", c.supply === "hhl_consigns" ? "font-medium text-accent-text" : "text-muted")} title={supplyText(c)}>
          {supplyText(c)}
        </span>
      ),
    },
    { key: "per_unit", header: "Per unit", width: "80px", align: "right", value: (c) => c.per_unit, cell: (c) => dim(c, qty(c.per_unit)) },
    { key: "need", header: "Need", headerTitle: "BOM quantity × units", width: "84px", align: "right", value: (c) => c.need, cell: (c) => dim(c, qty(c.need)) },
    {
      key: "allowance",
      header: "+ scrap",
      headerTitle: "Scrap allowance × units",
      width: "80px",
      align: "right",
      value: (c) => c.allowance,
      cell: (c) => dim(c, c.allowance ? qty(c.allowance) : "", "text-muted"),
    },
    { key: "total", header: "Total", width: "84px", align: "right", value: (c) => c.total, cell: (c) => dim(c, qty(c.total)) },
    {
      key: "consumed",
      header: "Used",
      width: "80px",
      align: "right",
      value: (c) => (c.consigned ? c.consumed : null),
      cell: (c) => dim(c, c.consigned && c.consumed ? qty(c.consumed) : "", "text-muted"),
    },
    {
      key: "remaining",
      header: "Still need",
      headerTitle: "What the units still to build take",
      width: "90px",
      align: "right",
      value: (c) => (c.consigned ? c.remaining : null),
      cell: (c) => dim(c, c.consigned ? qty(c.remaining) || "0" : ""),
    },
    {
      key: "atCm",
      header: "At CM",
      width: "84px",
      align: "right",
      value: (c) => c.atCm,
      cell: (c) => dim(c, c.atCm == null ? "" : qty(c.atCm) || "0", "text-muted"),
    },
    {
      key: "reserved",
      header: "Reserved",
      headerTitle: "Set aside for this order from the CM's stock, in priority order",
      width: "90px",
      align: "right",
      value: (c) => (c.consigned ? c.reserved : null),
      cell: (c) => dim(c, c.consigned && c.reserved ? qty(c.reserved) : "", "text-muted"),
    },
    {
      key: "short",
      header: "Short",
      width: "90px",
      align: "right",
      value: (c) => (c.short > 0 ? c.short : null),
      text: (c) => (c.short > 0 ? c.short : c.shortConfirmed > 0 ? "in transit" : ""),
      cell: (c) => (
        <span className={cx("truncate font-mono text-[12px]", c.short > 0 ? "text-danger" : c.shortConfirmed > 0 ? "text-accent-text" : "")}>
          {c.short > 0 ? qty(c.short) : c.shortConfirmed > 0 ? <span title="Covered once what's in transit arrives">in transit</span> : ""}
        </span>
      ),
    },
    // The BOM's order (what it's sorted by until someone sorts it another way).
    { key: "position", header: "#", optional: true, width: "48px", align: "right", value: (c) => rows.indexOf(c) + 1 },
  ];
  return (
    <div className="px-4 pt-2">
      <div className="flex items-center gap-2 pb-1">
        <span className="text-[11px] text-faint">Parts</span>
      </div>
      <DataTable fit rows={rows} columns={columns} defaultSort={{ key: "position" }} rowNoun={["part", "parts"]} exportName="Build parts" pageFilters={false} />
    </div>
  );
}

function ReportForm({ mo, state, itemById }: { mo: MakeOrder; state: MakeOrderState | undefined; itemById: Map<string, Item> }) {
  const [n, setN] = useState("");
  const [notes, setNotes] = useState("");
  const [scrap, setScrap] = useState<{ item: Item; qty: string; reason: ScrapReason }[]>([]);
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(() => crypto.randomUUID());
  const parts = useMemo(() => new Set((state?.components ?? []).filter((c) => c.consigned).map((c) => c.item_id)), [state]);

  const submit = async (allowOver = false) => {
    const count = parseQty(n);
    if (!count || count <= 0 || !Number.isInteger(count)) return toast.error("Enter the whole units built");
    const bad = scrap.find((s) => !((parseQty(s.qty) ?? 0) > 0));
    if (bad) return toast.error(`Enter how many ${bad.item.sku} were scrapped`);
    const over = state && state.output + count > state.qty;
    if (over && !allowOver) {
      if (!confirm(`That makes ${qty(state.output + count)} built of ${qty(state.qty)} ordered. Report it anyway?`)) return;
      return submit(true);
    }
    setBusy(true);
    const { ok, result } = await mutateWithResult("make_order.report_output", {
      id,
      make_order_id: mo.id,
      qty: count,
      notes: notes.trim(),
      allow_over: allowOver || undefined,
      scrap: scrap.length ? scrap.map((s) => ({ item_id: s.item.id, qty: parseQty(s.qty) as number, reason: s.reason })) : undefined,
    });
    setBusy(false);
    if (!ok) return;
    const r = result as { output: number; warnings?: string[] };
    toast.ok(`${qty(r.output)} built so far`);
    for (const w of r.warnings ?? []) toast.info(w);
    setN("");
    setNotes("");
    setScrap([]);
    setId(crypto.randomUUID());
  };

  return (
    <div className="mx-4 my-2 rounded-md border border-line bg-surface-2/40 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-muted">The CM built</span>
        <div className="w-24">
          <QtyInput aria-label="Units built" value={n} onChange={setN} />
        </div>
        <span className="text-[12px] text-muted">more</span>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (their report, batch)" className="h-7 min-w-40 flex-1 text-[12px]" />
        <ItemPicker
          className="w-56"
          allow={(i) => parts.has(i.id) && !scrap.some((s) => s.item.id === i.id)}
          onPick={(item) => setScrap((s) => [...s, { item, qty: "", reason: "defective" }])}
          placeholder="+ scrap a part…"
        />
        <Button size="sm" variant="primary" disabled={busy} onClick={() => submit()} title="Consumes its parts at the CM and records the finished goods there">
          Report output
        </Button>
      </div>
      {scrap.map((s) => (
        <div key={s.item.id} className="mt-1 flex items-center gap-2 text-[12px]">
          <span className="w-28 truncate font-mono">{itemById.get(s.item.id)?.sku}</span>
          <span className="min-w-0 flex-1 truncate">{s.item.name}</span>
          <span className="text-muted">scrapped beyond the allowance</span>
          <div className="w-20">
            <QtyInput
              aria-label={`${s.item.sku} scrapped`}
              value={s.qty}
              onChange={(t) => setScrap((all) => all.map((x) => (x.item.id === s.item.id ? { ...x, qty: t } : x)))}
            />
          </div>
          <div className="w-36 shrink-0">
            <Select
              aria-label="Reason"
              value={s.reason}
              onChange={(e) => setScrap((all) => all.map((x) => (x.item.id === s.item.id ? { ...x, reason: e.target.value as ScrapReason } : x)))}
              options={reasonOptions}
              className="h-7 text-[12px]"
            />
          </div>
          <button type="button" className="text-faint hover:text-danger" onClick={() => setScrap((all) => all.filter((x) => x.item.id !== s.item.id))}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
